import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle, withKeys as _withKeys } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 0 }

import { App } from "@src-capacitor/@capacitor/app"
import { PluginListenerHandle } from "@src-capacitor/@capacitor/core"
import { DialogChainObject, QCard, useDialogPluginComponent } from "quasar"
import SfxCardNotificationSystem from "src/components/card/SfxCardNotificationSystem.vue"
import SfxCardNotificationUser from "src/components/card/SfxCardNotificationUser.vue"
import SfxEmptyPage from "src/components/empty/SfxEmptyPage.vue"
import SfxTab from "src/components/navigation/SfxTab.vue"
import SfxTabs from "src/components/navigation/SfxTabs.vue"
import SfxTopNavigation from "src/components/navigation/SfxTopNavigation.vue"
import { useNotificationsStore } from "src/stores/notificationsStore"
import { ContextMenuItem } from "src/util/classes/contextMenuItem"
import { EmptyStates } from "src/util/constants/emptyStates"
import { ContextAction, MissingEntityType } from "src/util/enums/entityType"
import { NavigationAction } from "src/util/enums/topNavigation"
import { confirmationDialog, showFeedback, systemDialog } from "src/util/functions/dialogs"
import { helpTourNotImplemented } from "src/util/functions/helpTour"
import {
  compareData,
  filterOldNotifications,
  isSystemNotification,
  showRevertSelectionNotification,
  showSuccessNotification,
} from "src/util/functions/notifications"
import { getNotificationMockData } from "src/util/mockData/notifications"
import {
  NotificationCardData,
  SystemNotificationCardData,
  UserNotificationCardData,
} from "src/util/types/notifications"
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxOverlayNotifications',
  emits: [...useDialogPluginComponent.emits],
  setup(__props) {



//endregion

//region Variables

const { t } = useI18n()
const notificationsStore = useNotificationsStore()

const pageTitle = t("sfxOverlayNotifications.title")
const topNavigationContainer = ref<HTMLDivElement | null>(null)

const readTab = t("sfxOverlayNotifications.read_notifications")
const unreadTab = t("sfxOverlayNotifications.unread_notifications")
const activeTab = ref(unreadTab)

const unreadCount = computed(() => `${unreadTab} (${unreadNotifications.value.length})`)

const tabs = reactive([
  { name: unreadTab, label: unreadCount },
  { name: readTab, label: readTab },
])

const quickAction = computed(() => {
  const result = ref<ContextAction>()
  if (activeTab.value === readTab) {
    if (readNotifications.value.length > 0) {
      result.value = ContextAction.MARK_ALL_UNREAD
    }
  } else {
    if (unreadNotifications.value.length > 0) {
      result.value = ContextAction.MARK_ALL_READ
    }
  }
  return result.value
})

const scrollAreaHeight = computed(() => {
  const topNavigationHeight = topNavigationContainer.value?.offsetHeight || 0
  return { height: `calc(100% - ${topNavigationHeight}px)` }
})

const { dialogRef, onDialogCancel } = useDialogPluginComponent()

const allNotifications = ref<Array<NotificationCardData>>(
  getNotificationMockData().filter((notification: NotificationCardData) =>
    filterOldNotifications(notification)
  )
)

const readNotifications = computed(() => {
  return allNotifications.value.filter((notification) => notification.isRead).sort(compareData)
})

const unreadNotifications = computed(() => {
  return allNotifications.value.filter((notification) => !notification.isRead).sort(compareData)
})

// selectedNotificationsList is an array of pointers to allNotifications
const selectedNotificationsList = ref<NotificationCardData[]>([])

const isMultipleSelectionOn = computed(() => selectedNotificationsList.value.length > 0)
const showUndo = ref<DialogChainObject>()

let androidBackButtonListener: PluginListenerHandle | undefined

//endregion

//region Lifecycle

if (process.env.MODE === "capacitor") {
  onMounted(async () => {
    androidBackButtonListener = await App.addListener("backButton", async () => {
      if (selectedNotificationsList.value.length === 0) {
        if (showUndo.value) showUndo.value?.hide()
        await androidBackButtonListener?.remove()
        onDialogCancel()
      } else {
        deselect()
      }
    })
  })

  onUnmounted(async () => {
    await androidBackButtonListener?.remove()
  })
}

//endregion

//region Functions

function addBorder(notification: NotificationCardData) {
  return selectedNotificationsList.value.includes(notification) ? "sfx-card--selected" : undefined
}

async function handleContextSelect(contextMenuItem: ContextMenuItem) {
  switch (contextMenuItem.action) {
    case ContextAction.FEEDBACK:
      return showFeedback({ context: pageTitle })
    case ContextAction.HELP:
      return helpTourNotImplemented("NotificationsOverlay")
    case ContextAction.MARK_ALL_READ:
      return await markRead()
    case ContextAction.MARK_ALL_UNREAD:
      return await markUnread()
  }
}

async function markRead() {
  if (selectedNotificationsList.value.length > 0) {
    // If there are selected notifications - only act on those
    selectedNotificationsList.value.map((notification) => (notification.isRead = true))
    selectedNotificationsList.value.length = 0
  } else {
    // Otherwise, but ask for confirmation and act on all
    const userConfirmed = await confirmationDialog({
      title: t("sfxOverlayNotifications.mark_read_dialogTitle"),
      content: t("sfxOverlayNotifications.mark_read_dialogContent"),
      persistent: true,
    })
    if (userConfirmed) {
      allNotifications.value.map((notification) => (notification.isRead = true))
    }
  }
}

async function markUnread() {
  if (selectedNotificationsList.value.length > 0) {
    // If there are selected notifications - only act on those
    selectedNotificationsList.value.map((notification) => (notification.isRead = false))
    selectedNotificationsList.value.length = 0
  } else {
    // Otherwise, but ask for confirmation and act on all
    const userConfirmed = await confirmationDialog({
      title: t("sfxOverlayNotifications.mark_unread_dialogTitle"),
      content: t("sfxOverlayNotifications.mark_unread_dialogContent"),
      persistent: true,
    })
    if (userConfirmed) {
      allNotifications.value.map((notification) => (notification.isRead = false))
    }
  }
}

function handleNotificationClick(notification: NotificationCardData) {
  if (isMultipleSelectionOn.value) {
    // If multi-select is on, select multiple notifications onclick
    selectMultipleNotifications(notification)
  } else {
    // Otherwise, check: If system notification - open system dialog, else - go to the relation
    notification.isSystemCardNotification
      ? showSystemDialog(notification as SystemNotificationCardData)
      : goToRelation(notification as UserNotificationCardData)
  }
}

function selectMultipleNotifications(notification: NotificationCardData) {
  if (selectedNotificationsList.value.includes(notification)) {
    // If already selected - deselect it
    const idx = selectedNotificationsList.value.indexOf(notification)
    selectedNotificationsList.value.splice(idx, 1)
  } else {
    // Otherwise - select it
    selectedNotificationsList.value.push(notification)
  }
}

async function showSystemDialog(notification: SystemNotificationCardData) {
  const userConfirmed = await systemDialog(notification)
  if (userConfirmed) {
    // On confirmation - mark the notification as read
    const idxAll = allNotifications.value.indexOf(notification)
    allNotifications.value[idxAll].isRead = true
    // TODO: Uncomment the following two lines when the real data is incorporated into the page(won't work with mock data)
    // TODO: Until that there would be some problems with store's system notification content
    // Update the system notifications in the notification store
    // const idxSys = notificationsStore.sysNotifications.indexOf(notification)
    // notificationsStore.sysNotifications.splice(idxSys, 1)
  }
}

function goToRelation(notification: UserNotificationCardData) {
  // Mark the notification as read
  const idx = allNotifications.value.indexOf(notification)
  allNotifications.value[idx].isRead = true

  // TODO: Go to specific relation when we have those implemented
  const message = "Relation: " + notification.relationRepresentation
  showSuccessNotification(message)
}

/**
 * The function notifies the user that he might lose his multiple
 * selection of notifications and gives him the option to bring those back,
 * in case of a miss-click.
 *
 * After the time has elapsed, the selected notifications will be deselected.
 */
function deselect() {
  if (selectedNotificationsList.value.length === 0) return

  if (activeTab.value === unreadTab) {
    // Backup previously selected notifications
    const backupArray: NotificationCardData[] = Array.from(selectedNotificationsList.value)

    // Show undo notification with the option to bring back previous selection
    showUndo.value = showRevertSelectionNotification(() => {
      selectedNotificationsList.value = backupArray
      activeTab.value = unreadTab
    })
  } else {
    // Backup previously selected notifications
    const backupArray: NotificationCardData[] = Array.from(selectedNotificationsList.value)

    // Show undo notification with the option to bring back previous selection
    showUndo.value = showRevertSelectionNotification(() => {
      selectedNotificationsList.value = backupArray
      activeTab.value = readTab
    })
  }
  selectedNotificationsList.value.length = 0
}

function switchTab(currentTab: string) {
  deselect()
  activeTab.value = currentTab
}

//endregion

//region Setup Code

watch(
  () => unreadNotifications.value,
  (notifications) => {
    if (notifications) {
      notificationsStore.count = notifications.length
    }

    notificationsStore.sysNotifications = notifications.filter(
      (notification): notification is SystemNotificationCardData =>
        isSystemNotification(notification)
    )
  }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_tab_panel = _resolveComponent("q-tab-panel")!
  const _component_q_tab_panels = _resolveComponent("q-tab-panels")!
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_scroll_area = _resolveComponent("q-scroll-area")!
  const _component_q_dialog = _resolveComponent("q-dialog")!
  const _directive_touch_hold = _resolveDirective("touch-hold")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    "transition-show": "slide-up",
    "transition-hide": "slide-down",
    class: "full-height",
    maximized: "",
    onKeyup: _withKeys(deselect, ["esc"])
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(QCard), { class: "full-height" }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            ref_key: "topNavigationContainer",
            ref: topNavigationContainer
          }, [
            _createVNode(SfxTopNavigation, {
              title: _unref(pageTitle),
              "navigation-action": _unref(NavigationAction).CLOSE,
              "quick-action": quickAction.value,
              onContextSelect: handleContextSelect,
              "hide-notifications": "",
              "hide-separator": "",
              onClose: _unref(onDialogCancel),
              class: "shadow"
            }, null, 8, ["title", "navigation-action", "quick-action", "onClose"]),
            _createVNode(SfxTabs, {
              "model-value": activeTab.value,
              "onUpdate:modelValue": switchTab,
              class: "q-px-md"
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs, (tab) => {
                  return (_openBlock(), _createBlock(SfxTab, {
                    key: tab,
                    label: tab.label,
                    name: tab.name,
                    class: "full-width"
                  }, null, 8, ["label", "name"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["model-value"])
          ], 512),
          _createVNode(_component_q_scroll_area, {
            class: "full-width scrollbar-area",
            style: _normalizeStyle(scrollAreaHeight.value),
            visible: false
          }, {
            default: _withCtx(() => [
              _createVNode(_component_q_card_section, null, {
                default: _withCtx(() => [
                  _createVNode(_component_q_tab_panels, {
                    "model-value": activeTab.value,
                    "onUpdate:modelValue": switchTab,
                    swipeable: "",
                    animated: "",
                    "transition-prev": "fade",
                    "transition-next": "fade",
                    class: "full-height",
                    "keep-alive": ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_q_tab_panel, {
                        name: _unref(unreadTab),
                        class: "q-pa-none"
                      }, {
                        default: _withCtx(() => [
                          (unreadNotifications.value.length > 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(unreadNotifications.value, (notification) => {
                                  return (_openBlock(), _createElementBlock(_Fragment, { key: notification }, [
                                    (notification.isSystemCardNotification)
                                      ? _withDirectives((_openBlock(), _createBlock(SfxCardNotificationSystem, {
                                          key: 0,
                                          data: notification,
                                          onClick: ($event: any) => (handleNotificationClick(notification)),
                                          class: _normalizeClass(addBorder(notification))
                                        }, null, 8, ["data", "onClick", "class"])), [
                                          [
                                            _directive_touch_hold,
                                            () => selectMultipleNotifications(notification),
                                            void 0,
                                            { mouse: true }
                                          ]
                                        ])
                                      : _withDirectives((_openBlock(), _createBlock(SfxCardNotificationUser, {
                                          key: 1,
                                          data: notification,
                                          onClick: ($event: any) => (handleNotificationClick(notification)),
                                          class: _normalizeClass(addBorder(notification))
                                        }, null, 8, ["data", "onClick", "class"])), [
                                          [
                                            _directive_touch_hold,
                                            () => selectMultipleNotifications(notification),
                                            void 0,
                                            { mouse: true }
                                          ]
                                        ])
                                  ], 64))
                                }), 128))
                              ]))
                            : (_openBlock(), _createBlock(SfxEmptyPage, {
                                key: 1,
                                class: "full-width q-px-lg",
                                missingEntity: _unref(EmptyStates).get(_unref(MissingEntityType).NOTIFICATION_NOT_FOUND),
                                "vertical-center": ""
                              }, null, 8, ["missingEntity"]))
                        ]),
                        _: 1
                      }, 8, ["name"]),
                      _createVNode(_component_q_tab_panel, {
                        name: _unref(readTab),
                        class: "q-pa-none"
                      }, {
                        default: _withCtx(() => [
                          (readNotifications.value.length > 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(readNotifications.value, (notification) => {
                                  return (_openBlock(), _createElementBlock(_Fragment, { key: notification }, [
                                    (notification.isSystemCardNotification)
                                      ? _withDirectives((_openBlock(), _createBlock(SfxCardNotificationSystem, {
                                          key: 0,
                                          data: notification,
                                          onClick: ($event: any) => (handleNotificationClick(notification)),
                                          class: _normalizeClass(addBorder(notification))
                                        }, null, 8, ["data", "onClick", "class"])), [
                                          [
                                            _directive_touch_hold,
                                            () => selectMultipleNotifications(notification),
                                            void 0,
                                            { mouse: true }
                                          ]
                                        ])
                                      : _withDirectives((_openBlock(), _createBlock(SfxCardNotificationUser, {
                                          key: 1,
                                          data: notification,
                                          onClick: ($event: any) => (handleNotificationClick(notification)),
                                          class: _normalizeClass(addBorder(notification))
                                        }, null, 8, ["data", "onClick", "class"])), [
                                          [
                                            _directive_touch_hold,
                                            () => selectMultipleNotifications(notification),
                                            void 0,
                                            { mouse: true }
                                          ]
                                        ])
                                  ], 64))
                                }), 128))
                              ]))
                            : (_openBlock(), _createBlock(SfxEmptyPage, {
                                key: 1,
                                class: "full-width q-px-lg",
                                missingEntity: _unref(EmptyStates).get(_unref(MissingEntityType).NOTIFICATION_NOT_FOUND),
                                "vertical-center": ""
                              }, null, 8, ["missingEntity"]))
                        ]),
                        _: 1
                      }, 8, ["name"])
                    ]),
                    _: 1
                  }, 8, ["model-value"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["style"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 512))
}
}

})